import { useCallback, useState } from "react";

function readCurrentCsrfTokenFromMetaTag(): string | null {
  const metaTag = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null;
  if (metaTag && typeof metaTag.content === "string") {
    return metaTag.content;
  }
  return null;
}

function extractCsrfTokenFromHtml(htmlDocument: string): string {
  const csrfMetaMatch = htmlDocument.match(
    /<meta\s+name=["']csrf-token["']\s+content=["']([^"']+)["']/i,
  );
  if (!csrfMetaMatch) {
    throw new Error("CSRF meta tag not found in HTML response.");
  }
  return csrfMetaMatch[1];
}

async function fetchCsrfTokenFromPath(pathThatRendersCsrfMeta: string): Promise<string> {
  const cacheBuster = Date.now();
  const response = await fetch(`${pathThatRendersCsrfMeta}?_=${cacheBuster}`, {
    credentials: "same-origin",
    cache: "no-store",
    headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" },
  });
  const htmlDocument = await response.text();
  return extractCsrfTokenFromHtml(htmlDocument);
}

export function useCsrfToken(pathThatRendersCsrfMeta: string = "/auth") {
  const [csrfToken, setCsrfToken] = useState<string | null>(readCurrentCsrfTokenFromMetaTag());

  const refreshCsrfToken = useCallback(async () => {
    const freshToken = await fetchCsrfTokenFromPath(pathThatRendersCsrfMeta);
    const csrfMetaTagElement = document.querySelector(
      'meta[name="csrf-token"]',
    ) as HTMLMetaElement | null;
    if (csrfMetaTagElement) {
      csrfMetaTagElement.setAttribute("content", freshToken);
    }
    setCsrfToken(freshToken);
    return freshToken;
  }, [pathThatRendersCsrfMeta]);

  return { csrfToken, refreshCsrfToken };
}
